import { ButtonCheckbox } from '@/components/common/Button'
import { InputTypeahead } from '@/components/common/Input'
import { OptionCount } from '@/components/common/OptionCount'
import { Icon } from '@/components/icons/Icon'
import { useIsNavigating } from '@/components/providers/NavigationProvider'
import { RESET_DEFAULT, type TypeaheadOptionType } from '@/defs/input'
import { SCROLL_GALLERY } from '@/defs/scroll'
import { useLinkInternal } from '@/hooks/useLinkInternal'

import styles from './FilterInputTypeahead.module.scss'

type OptionType = {
  count: number
  label: string
  slug: string
}

export type Props = {
  filter: 'preconCommanderSlug' | 'tagSlug'
  label: string
  optionSelected?: OptionType
  options: OptionType[]
  size?: 'sm'
}

export const FilterInputTypeahead = ({ filter, label, options: optionsProp, optionSelected, size }: Props) => {
  const { buildRelative } = useLinkInternal()
  const isNavigating = useIsNavigating()
  const { redirectRelative } = useLinkInternal()

  const isSm = size === 'sm'

  const optionsVisible = optionsProp
    .filter((option) => option.slug !== optionSelected?.slug)
    .slice(0, optionSelected ? 3 : 4)

  const options = optionsProp.map<TypeaheadOptionType<string>>(({ count, label, slug }) => ({
    Component: ({ children }) => (
      <div className={styles.option}>
        <div className={styles.optionLabel}>{children}</div>
        <OptionCount count={count} />
      </div>
    ),
    label,
    value: slug,
  }))

  const hasOptions = optionsVisible.length > 0

  const optionSelectedElement = optionSelected && (
    <li>
      <ButtonCheckbox
        as='LinkRoute'
        count={optionSelected.count}
        href={buildRelative({ [filter]: undefined })}
        scroll={SCROLL_GALLERY}
        selected
        showLoadingMask={isNavigating}
      >
        {optionSelected.label}
      </ButtonCheckbox>
    </li>
  )

  const itemDivider = optionSelected && (
    <li className={styles.divider}>
      <span className={styles.dividerIcon}>
        <Icon name='ellipsisV' />
      </span>
    </li>
  )

  const itemEmpty = (
    <li>
      <p className={styles.emptyMessage}>{`No ${label} found.`}</p>
    </li>
  )

  const itemOptions = optionsVisible.map((option, i) => (
    <li key={i}>
      <ButtonCheckbox
        as='LinkRoute'
        count={option.count}
        href={buildRelative({ [filter]: option.slug })}
        scroll={SCROLL_GALLERY}
        showLoadingMask={isNavigating}
      >
        {option.label}
      </ButtonCheckbox>
    </li>
  ))

  const itemTypeahead = options.length > 0 && (
    <li>
      <InputTypeahead
        aria-label={`Search for ${label}`}
        onSelect={(v) => redirectRelative({ [filter]: v }, { scroll: SCROLL_GALLERY })}
        options={options}
        placeholder={`More ${label}...`}
        reset={RESET_DEFAULT}
        showLoadingMask={isNavigating}
      />
    </li>
  )

  const itemTypeaheadSelected = (
    <li>
      <InputTypeahead
        aria-label={`Search ${label}...`}
        onClear={() => redirectRelative({ [filter]: undefined }, { scroll: SCROLL_GALLERY })}
        onSelect={(v) => redirectRelative({ [filter]: v }, { scroll: SCROLL_GALLERY })}
        options={options}
        placeholder={`Search ${label}...`}
        reset={RESET_DEFAULT}
        showLoadingMask={isNavigating}
        value={optionSelected?.slug || ''}
      />
    </li>
  )

  if (isSm) {
    return <ul className={styles.container}>{hasOptions || optionSelected ? itemTypeaheadSelected : itemEmpty}</ul>
  }

  return (
    <ul className={styles.container}>
      {optionSelectedElement}
      {hasOptions ? (
        <>
          {itemDivider}
          {itemOptions}
          {itemTypeahead}
        </>
      ) : (
        !optionSelected && itemEmpty
      )}
    </ul>
  )
}
